import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, ButtonGroup, Form, Modal, Spinner, StatusBadge, Tab, Tabs } from '@/components/ui';
import { useAuth } from '../../../contexts/AuthContext';
import type { User } from '../../../types/User';
import {
  fetchPcrSubmissionHistory,
  postPcrHistoryComment,
  type PcrHistoryNoteStoredV1,
  type PcrSubmission,
  type PcrSubmissionHistory,
} from '../../../services/pcrService';
import PcrContactLogPanel from './PcrContactLogPanel';
import { describeActionError } from '../../../utils/loadError';

export type PcrNotesHistoryTab = 'notes' | 'contact';

interface PcrNotesHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  submission: PcrSubmission | null;
  onCommentAdded?: () => void;
  initialTab?: PcrNotesHistoryTab;
  contactLogRefreshToken?: number;
}

function parsePcrHistoryNoteJson(comments: string | null | undefined): PcrHistoryNoteStoredV1 | null {
  if (!comments) return null;
  try {
    const parsed = JSON.parse(comments) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      (parsed as PcrHistoryNoteStoredV1)._format === 'pcr_note_v1' &&
      typeof (parsed as PcrHistoryNoteStoredV1).body === 'string'
    ) {
      return parsed as PcrHistoryNoteStoredV1;
    }
  } catch {
    /* not JSON */
  }
  return null;
}

/** Whether to show the WordPress-style contact metadata block. */
function isContactNoteForDisplay(s: PcrHistoryNoteStoredV1): boolean {
  if (s.kind === 'regular') return false;
  if (s.kind === 'contact') return true;
  const hasMeta = !!(
    s.callerContactedBy?.trim() ||
    s.contactedAt?.trim() ||
    s.contractorsRepresentative?.trim()
  );
  return hasMeta;
}

function formatStageLabel(stage: string): string {
  const labels: Record<string, string> = {
    created: 'Received',
    data_edit: 'Data edit',
    manual_comment: 'Supervisor note',
    promoted_to_work_id: 'Work ID',
    closed_without_work_id: 'Closed (no Work ID)',
    outbound_email: 'Reply sent',
  };
  return labels[stage] || stage.replace(/_/g, ' ');
}

function isOutboundEmailHistoryEntry(stage: string): boolean {
  return stage === 'outbound_email';
}

function isInternalOnlyHistoryComment(comments: string | null | undefined): boolean {
  if (!comments?.trim()) return false;
  return /^workIdId=\d+$/i.test(comments.trim());
}

function formatContactedDisplay(value: string | undefined): string {
  if (!value?.trim()) return '–';
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' });
  }
  return value;
}

/** Value for `<input type="datetime-local" />` using the browser's local timezone. */
function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Same sources as server middleware: RolePermission keys + role.access. */
function permissionKeySet(user: User | null): Set<string> {
  const keys = new Set<string>();
  if (!user) return keys;
  for (const k of user.access || []) {
    keys.add(k);
  }
  for (const p of user.permissions || []) {
    if (p?.key) keys.add(p.key);
  }
  return keys;
}

const PcrNotesHistoryModal: React.FC<PcrNotesHistoryModalProps> = ({
  visible,
  onClose,
  submission,
  onCommentAdded,
  initialTab = 'notes',
  contactLogRefreshToken = 0,
}) => {
  const { currentUser, loading: authLoading } = useAuth();
  const keys = useMemo(() => permissionKeySet(currentUser), [currentUser]);

  /** While auth is loading, allow the modal shell; then require view permission (matches GET /history). */
  const mayViewHistory =
    authLoading || keys.has('submission:view') || keys.has('pcr:history_view');
  /** Matches POST /history-comment: pcr:history_create, pcr:create, or submission:pcr_update. */
  const mayAddNote =
    !authLoading &&
    (keys.has('pcr:history_create') ||
      keys.has('pcr:create') ||
      keys.has('submission:pcr_update'));

  const [localHistory, setLocalHistory] = useState<PcrSubmissionHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [callerContactedBy, setCallerContactedBy] = useState('');
  const [contactedAt, setContactedAt] = useState('');
  const [contractorsRepresentative, setContractorsRepresentative] = useState('');
  const [noteKind, setNoteKind] = useState<'regular' | 'contact'>('regular');
  const [isFirstContact, setIsFirstContact] = useState(false);
  const [firstContactAt, setFirstContactAt] = useState('');
  const [firstContactError, setFirstContactError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<PcrNotesHistoryTab>('notes');

  const reloadHistory = useCallback(async () => {
    if (!submission?.id) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const rows = await fetchPcrSubmissionHistory(submission.id);
      setLocalHistory(rows);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Could not load history.');
      setLocalHistory([]);
    } finally {
      setIsLoading(false);
    }
  }, [submission?.id]);

  useEffect(() => {
    if (!visible || !submission?.id || authLoading || !mayViewHistory) return;
    void reloadHistory();
  }, [visible, submission?.id, authLoading, mayViewHistory, reloadHistory]);

  useEffect(() => {
    if (!visible) {
      setNote('');
      setCallerContactedBy('');
      setContactedAt('');
      setContractorsRepresentative('');
      setNoteKind('regular');
      setIsFirstContact(false);
      setFirstContactAt('');
      setFirstContactError(null);
      setSaveError(null);
      setLoadError(null);
      setLocalHistory([]);
      setActiveTab('notes');
      return;
    }
    setActiveTab(initialTab);
    setNoteKind('regular');
    setIsFirstContact(false);
    setFirstContactAt('');
    setContactedAt(toDatetimeLocalValue(new Date()));
  }, [visible, initialTab]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!submission?.id || !note.trim() || !mayAddNote) return;
    setSaveError(null);

    const firstAtResolved =
      noteKind === 'contact' && isFirstContact
        ? firstContactAt.trim() || contactedAt.trim() || ''
        : '';
    if (noteKind === 'contact' && isFirstContact && !firstAtResolved) {
      setFirstContactError('First contact time required – set it here or set the contacted date and time');
      document.getElementById('pcr-first-contact-at')?.focus();
      return;
    }

    try {
      setIsSubmitting(true);
      await postPcrHistoryComment(submission.id, {
        note: note.trim(),
        kind: noteKind,
        callerContactedBy:
          noteKind === 'contact' ? callerContactedBy.trim() || undefined : undefined,
        contactedAt: noteKind === 'contact' ? contactedAt.trim() || undefined : undefined,
        contractorsRepresentative:
          noteKind === 'contact' ? contractorsRepresentative.trim() || undefined : undefined,
        isFirstContact: noteKind === 'contact' && isFirstContact ? true : undefined,
        firstContactAt:
          noteKind === 'contact' && isFirstContact ? firstAtResolved : undefined,
      });
      setNote('');
      setCallerContactedBy('');
      setContractorsRepresentative('');
      setIsFirstContact(false);
      setFirstContactAt('');
      setContactedAt(toDatetimeLocalValue(new Date()));
      await reloadHistory();
      onCommentAdded?.();
    } catch (err) {
      console.error('Error adding PCR note:', err);
      setSaveError(describeActionError(err, "Couldn't save the note. Try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const sortedHistory = [...localHistory].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  if (!mayViewHistory) {
    return null;
  }

  const title = submission
    ? `PCR notes & history – ${submission.report_id || `ID ${submission.id}`}`
    : 'PCR notes & history';

  return (
    <Modal show={visible && !!submission} onHide={onClose} size="2xl">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!submission ? (
          <p className="tw-text-content-muted tw-text-sm tw-mb-0">No submission selected.</p>
        ) : (
          <>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab((k as PcrNotesHistoryTab) || 'notes')}
              className="tw-mb-3"
            >
              <Tab eventKey="notes" title="Notes history">
                <div style={{ maxHeight: '520px', overflowY: 'auto' }}>
                  {authLoading || isLoading ? (
                    <div className="tw-text-center tw-py-4">
                      <Spinner animation="border" role="status" variant="primary">
                        <span className="tw-sr-only">Loading…</span>
                      </Spinner>
                    </div>
                  ) : loadError ? (
                    <Alert variant="danger" className="tw-mb-0">
                      {loadError}
                    </Alert>
                  ) : sortedHistory.length === 0 ? (
                    <div className="tw-text-content-muted tw-text-sm">No history entries yet.</div>
                  ) : (
                    sortedHistory.map((entry) => {
                  const isReplySent = isOutboundEmailHistoryEntry(entry.stage);
                  const structured = isReplySent ? null : parsePcrHistoryNoteJson(entry.comments);
                  const when = new Date(entry.created_at).toLocaleString(undefined, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false,
                  });

                  return (
                    <div
                      key={entry.id}
                      className={`tw-border tw-border-line tw-rounded tw-bg-surface tw-relative ${isReplySent ? 'tw-p-2 tw-mb-2' : 'tw-p-3 tw-mb-3'}`}
                      style={{ borderColor: 'rgba(0,0,0,0.12)' }}
                    >
                      <div
                        className={`tw-flex tw-justify-between tw-items-start tw-gap-2 ${isReplySent ? 'tw-mb-0' : 'tw-mb-2'}`}
                      >
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                          <span className="tw-font-semibold">{entry.summary || formatStageLabel(entry.stage)}</span>
                          <StatusBadge status={entry.stage} vocabulary="pcr" label={formatStageLabel(entry.stage)} />
                        </div>
                        <span className="tw-text-sm tw-font-semibold tw-whitespace-nowrap">{when}</span>
                      </div>

                      {structured ? (
                        <>
                          <p className="tw-mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                            &quot;{structured.body}&quot;
                          </p>
                          {isContactNoteForDisplay(structured) ? (
                            <div className="tw-border-t tw-border-line tw-pt-2 tw-mt-2">
                              <div className="tw-font-semibold tw-underline tw-text-sm tw-mb-2">Information:</div>
                              <ul className="tw-list-none tw-pl-0 tw-text-sm tw-mb-0">
                                <li>
                                  <span className="tw-text-content-muted">Reporter contacted by:</span>{' '}
                                  {structured.callerContactedBy?.trim()
                                    ? structured.callerContactedBy
                                    : 'No contact'}
                                </li>
                                <li>
                                  <span className="tw-text-content-muted">Contacted date and time:</span>{' '}
                                  {formatContactedDisplay(structured.contactedAt)}
                                </li>
                                <li>
                                  <span className="tw-text-content-muted">Contractor&apos;s representative:</span>{' '}
                                  {structured.contractorsRepresentative?.trim() || '–'}
                                </li>
                                {(structured.isFirstContact || structured.firstContactAt?.trim()) && (
                                  <li>
                                    <span className="tw-text-content-muted">First contact for this PCR:</span>{' '}
                                    <strong>{structured.isFirstContact ? 'Yes' : '–'}</strong>
                                    {(structured.firstContactAt || structured.contactedAt) && (
                                      <span className="tw-ml-1">
                                        – recorded at{' '}
                                        {formatContactedDisplay(
                                          structured.firstContactAt || structured.contactedAt,
                                        )}
                                      </span>
                                    )}
                                  </li>
                                )}
                              </ul>
                            </div>
                          ) : null}
                        </>
                      ) : isReplySent ? null : (
                        <>
                          {entry.comments && !isInternalOnlyHistoryComment(entry.comments) ? (
                            <p className="tw-mb-2 tw-text-sm" style={{ whiteSpace: 'pre-wrap' }}>
                              {entry.comments}
                            </p>
                          ) : null}
                        </>
                      )}

                      <div className={`tw-text-sm tw-text-content-muted ${isReplySent ? 'tw-mt-1' : 'tw-mt-2'}`}>
                        {entry.user_name?.trim()
                          ? `By ${entry.user_name}`
                          : entry.external
                            ? 'External (website / webhook)'
                            : 'System'}
                      </div>
                    </div>
                  );
                })
                  )}
                </div>
              </Tab>
              <Tab eventKey="contact" title="Contact log">
                <PcrContactLogPanel
                  submissionId={submission.id}
                  enabled={activeTab === 'contact'}
                  maxHeight="520px"
                  refreshToken={contactLogRefreshToken}
                />
              </Tab>
            </Tabs>
          </>
        )}
      </Modal.Body>

      {/*
        The composer lives in the footer (pinned, always visible) rather than a second column
        beside the tabs. On a phone that second column used to sit BELOW the history list, so
        adding a comment meant scrolling past it first — the footer's own top border/padding is
        what gives it the "slight margin" separation from the history above, for free.
      */}
      {submission && (
        <Modal.Footer>
          <div style={{ width: '100%' }}>
            {authLoading ? (
              <div className="tw-flex tw-items-center tw-justify-center tw-py-1">
                <Spinner animation="border" size="sm" role="status" />
                <span className="tw-ml-2 tw-text-content-muted tw-text-sm">Checking permissions…</span>
              </div>
            ) : mayAddNote ? (
              <Form onSubmit={handleSubmit} autoComplete="off" noValidate>
                {saveError && (
                  <Alert variant="danger" role="alert" className="tw-mb-2">
                    {saveError}
                  </Alert>
                )}
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-mb-2">
                  <span className="tw-text-sm tw-font-semibold tw-text-content-muted">Add note</span>
                  <ButtonGroup role="group" aria-label="Note type">
                    <Button
                      type="button"
                      id="pcr-note-kind-regular"
                      size="sm"
                      variant={noteKind === 'regular' ? 'primary' : 'outline-secondary'}
                      className="tw-flex tw-items-center tw-gap-1"
                      aria-pressed={noteKind === 'regular'}
                      onClick={() => {
                        setNoteKind('regular');
                        setIsFirstContact(false);
                        setFirstContactAt('');
                      }}
                    >
                      <i className="bi bi-journal-text" aria-hidden />
                      <span>Regular</span>
                    </Button>
                    <Button
                      type="button"
                      id="pcr-note-kind-contact"
                      size="sm"
                      variant={noteKind === 'contact' ? 'primary' : 'outline-secondary'}
                      className="tw-flex tw-items-center tw-gap-1"
                      aria-pressed={noteKind === 'contact'}
                      onClick={() => {
                        setNoteKind('contact');
                        setContactedAt(toDatetimeLocalValue(new Date()));
                      }}
                    >
                      <i className="bi bi-person-lines-fill" aria-hidden />
                      <span>Contact</span>
                    </Button>
                  </ButtonGroup>
                </div>
                <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 tw-items-start">
                  <Form.Group className="tw-w-full tw-mb-0" controlId="pcr-note-body">
                    <Form.Control
                      as="textarea"
                      rows={noteKind === 'contact' ? 3 : 2}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder={
                        noteKind === 'contact'
                          ? 'Describe the contact or follow-up…'
                          : 'Add an internal note about this PCR…'
                      }
                      required
                    />
                  </Form.Group>
                  <Button
                    type="submit"
                    variant="primary"
                    className="tw-shrink-0 tw-w-full sm:tw-w-auto"
                    disabled={!note.trim() || isSubmitting}
                  >
                    {isSubmitting ? 'Saving…' : noteKind === 'contact' ? 'Save contact note' : 'Save note'}
                  </Button>
                </div>
                {noteKind === 'contact' ? (
                  // Capped and independently scrollable so an expanded contact note (first-contact
                  // fields included) can never grow the footer past the modal's own height.
                  <div className="tw-mt-2 tw-max-h-40 tw-overflow-y-auto tw-space-y-2 tw-border-t tw-border-line tw-pt-2 tw-pr-1">
                    <Form.Group className="tw-mb-0" controlId="pcr-note-caller-contacted">
                      <Form.Label className="tw-text-sm">Reporter contacted by</Form.Label>
                      <Form.Control
                        type="text"
                        value={callerContactedBy}
                        onChange={(e) => setCallerContactedBy(e.target.value)}
                        placeholder="e.g. No contact"
                      />
                    </Form.Group>
                    <Form.Group className="tw-mb-0" controlId="pcr-note-contacted-at">
                      <Form.Label className="tw-text-sm">Contacted date and time</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={contactedAt}
                        onChange={(e) => {
                          setContactedAt(e.target.value);
                          setFirstContactError(null);
                        }}
                      />
                    </Form.Group>
                    <Form.Group className="tw-mb-0" controlId="pcr-first-contact-flag">
                      <Form.Check
                        type="checkbox"
                        id="pcr-first-contact"
                        label="First contact for this PCR"
                        checked={isFirstContact}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setIsFirstContact(on);
                          if (!on) {
                            setFirstContactAt('');
                            setFirstContactError(null);
                          }
                        }}
                      />
                      <Form.Text className="tw-text-content-muted tw-block tw-text-sm">
                        Turn this on only when this note documents the first contact. If checked, set &quot;First contact
                        at&quot; below or rely on contacted date/time when saving.
                      </Form.Text>
                    </Form.Group>
                    {isFirstContact ? (
                      <Form.Group className="tw-mb-0" controlId="pcr-first-contact-at">
                        <Form.Label className="tw-text-sm">First contact at</Form.Label>
                        <Form.Control
                          type="datetime-local"
                          value={firstContactAt}
                          onChange={(e) => {
                            setFirstContactAt(e.target.value);
                            setFirstContactError(null);
                          }}
                          isInvalid={!!firstContactError}
                        />
                        {firstContactError && (
                          <Form.Control.Feedback type="invalid">{firstContactError}</Form.Control.Feedback>
                        )}
                        <Form.Text className="tw-text-content-muted">
                          Optional: specific time for first contact. If left blank, contacted date/time is used when you
                          save.
                        </Form.Text>
                      </Form.Group>
                    ) : null}
                    <Form.Group className="tw-mb-0" controlId="pcr-note-contractor-rep">
                      <Form.Label className="tw-text-sm">Contractor&apos;s representative</Form.Label>
                      <Form.Control
                        type="text"
                        value={contractorsRepresentative}
                        onChange={(e) => setContractorsRepresentative(e.target.value)}
                      />
                    </Form.Group>
                  </div>
                ) : null}
              </Form>
            ) : (
              <Alert variant="warning" className="tw-mb-0 tw-text-sm">
                You don&apos;t have access to add PCR notes. Ask your administrator if you need it.
              </Alert>
            )}
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default PcrNotesHistoryModal;
